import React, { useState, useEffect, useRef, useLayoutEffect, useId } from 'react'
import { Expand } from 'components/icons'
import Button from 'components/button'
import Tooltip from 'components/tooltip'
import {
  createCellId,
  getRowHeightVariable,
  getRowHeightClassName,
  getRowMaxHeightClassName,
  validateField,
  isSameValueStringValue
} from 'components/spreadsheet/helpers/functions'
import { checkValuesSame } from 'components/spreadsheet/helpers/paste'
import { TextCell } from 'components/spreadsheet/components/cell/components/text'
import { NumberCell } from 'components/spreadsheet/components/cell/components/number'
import { DateCell } from 'components/spreadsheet/components/cell/components/date'
import { LinkCell } from 'components/spreadsheet/components/cell/components/link'
import { MultiLinkCell } from 'components/spreadsheet/components/cell/components/multilink'
import { VoteCell } from 'components/spreadsheet/components/cell/components/vote'
import { CheckboxCell } from 'components/spreadsheet/components/cell/components/checkbox'
import { SelectCell } from 'components/spreadsheet/components/cell/components/select'
import { AttachmentCell } from 'components/spreadsheet/components/cell/components/attachment'
import JsonCell from 'components/spreadsheet/components/cell/components/json'
import { CellIssue, CellIssueTone } from 'components/spreadsheet/components/cell/components/issue'
import {
  IFile,
  ICellValue,
  IJsonCellValue,
  IVote,
  IContextMenuState,
  ITableViewColumn,
  ITableViewColour,
  ITableRow,
  IRowLevelColour,
  IUpdateTableViewCell
} from 'types'
import { getCellColour, ROW_HOVER_FILL } from 'components/spreadsheet/helpers/colouring'
import {
  TEXT_COLUMN_TYPES,
  NUMBER_COLUMN_TYPES,
  DATE_COLUMN_TYPES,
  ATTACHMENT_COLUMN_TYPES,
  FRAMELESS_FORM_KINDS,
  SELECT_COLUMN_TYPES,
  INPUT_COLUMN_OPTION_TYPES,
  VOTE_COLUMN_TYPES,
  INITIAL_SELECTED_CELL_RANGE,
  INITIAL_SELECTED_CELL,
  ViewTypes
} from 'components/spreadsheet/constants/const'
import { useApplicationStore } from 'hooks/application'
import { useProject } from 'hooks/useProject'
import {
  IColumnTypes,
  ISelectedCell,
  IResultFieldValidation,
  ISelectedCellState,
  IEditCompleteOptions,
  ISelectedCellRange,
  IColumnValuesCount
} from 'components/spreadsheet/types'
import { isValidValue } from 'components/spreadsheet/helpers/validation'
import { getDuplicateValueKey, isCaseInsensitiveDuplicateKind } from 'components/spreadsheet/helpers/duplicates'
import { cellStyles } from 'components/spreadsheet/components/cell/styles'
import { useThemedColours } from 'hooks/useTheme'

const styles = cellStyles()

interface CellProps {
  isContributor: boolean
  isAdmin: boolean
  kind: IColumnTypes
  scriptEnabled: boolean
  formulaEnabled: boolean
  isJoined: boolean
  value: ICellValue
  frozen: boolean
  uniqueNumber: number
  rowHeight: number
  width?: number
  cumulativeWidth?: number
  locked?: boolean
  readOnly?: boolean
  loading?: boolean
  setSelectedCell?: (selectedCell: ISelectedCellState) => void
  rowNumber?: number
  rowId?: string
  nextRowId?: string
  prevRowId?: string
  columnNumber?: number
  columnName?: string
  columnId?: string
  nextColumnId?: string
  prevColumnId?: string
  align?: 'center' | 'left' | 'right'
  color?: string
  contextClick?: (menuState: IContextMenuState) => void
  canDrag?: boolean
  handleKeyDown?: (event: React.KeyboardEvent, selectedCell: ISelectedCell) => void
  setCellValue?: (cells: IUpdateTableViewCell, onSuccess: () => void, onError: (message?: string) => void) => void
  searched?: boolean
  isLastFrozenCell?: boolean
  selected?: boolean
  inSelectedCellRange?: boolean
  inSelectedColumn?: boolean
  inSelectedRow?: boolean
  inFillRange?: boolean
  isFillDragging?: boolean
  setSelectedCellRange?: (selectedCellRange: ISelectedCellRange) => void
  isExpanded?: boolean
  unresolvedComments?: number
  noComments?: number
  isFirstRow?: boolean
  isLastRow?: boolean
  useGreyBackground?: boolean
  /** The row is hovered: the cell takes the lighter hover fill (read-only cells stay grey). */
  hovered?: boolean
  showExpandIcon?: boolean
  formValues?: Record<string, ICellValue>
  editing: boolean
  tempValue?: ICellValue
  setExpandModal?: (expandModal: boolean, focusInput: boolean, rowId: string, rowNumber: number) => void
  setIsFocused?: (isFocused: boolean) => void
  setIsDragging?: (isDragging: boolean) => void
  rowData?: {
    [columnId: string]: ICellValue
  }
  column?: ITableViewColumn | null
  columnValuesCount: IColumnValuesCount
  colourSettings: ITableViewColour[]
  type: ViewTypes
  startRowId?: string
  finalRowId?: string
  finalRowNumber?: number
  startColumnId?: string
  finalColumnId?: string
  finalColumnNumber?: number
  allowComments: boolean
  row: ITableRow | null
  className?: string
  rowLevelColour?: IRowLevelColour | null
  // Transposed-view Field cell only: paint with the original column's header colours
  // so the row label reads as a header. Wins over colour rules and locked-grey defaults.
  headerColourOverride?: { backgroundColour: string; fontColour: string } | null
  // Tree group view: padding-left (px) applied to the cell content via the --cell-pl
  // CSS variable. Used to indent the tree-leaf cell so each row aligns under its
  // deepest visible group header without disturbing the cell's outer geometry.
  treeIndentPx?: number
  // Row-number cell only: build the whole-row selection from a click on the row number.
  // Mirrors the column header's ``handleColumnHeaderSelect`` call.
  onRowNumberSelect?: (modifiers: { shiftKey: boolean; toggleKey: boolean }) => void
  // Row-number cell only: reconcile the row selection and open the row menu on it.
  openRowMenu?: (event: React.MouseEvent) => void
  // External "this cell has an issue" trigger — used by matrix views to flag
  // cells that aggregated more than one source row. When set, the cell is
  // locked and shows an info CellIssue mark with this string as its message,
  // regardless of ``column.displayValidationError``.
  cellIssue?: string | null
}

const Cell: React.FC<CellProps> = ({
  isContributor,
  isAdmin,
  width,
  cumulativeWidth,
  rowHeight,
  rowNumber,
  rowId,
  nextRowId,
  prevRowId,
  setSelectedCell,
  setIsFocused,
  setIsDragging,
  columnNumber,
  columnName,
  columnId,
  prevColumnId,
  nextColumnId,
  uniqueNumber,
  kind,
  scriptEnabled,
  formulaEnabled,
  isJoined,
  value,
  locked,
  readOnly,
  loading,
  align,
  color,
  contextClick,
  frozen,
  canDrag,
  handleKeyDown,
  setCellValue,
  searched,
  isLastFrozenCell,
  selected,
  inSelectedCellRange,
  inSelectedColumn,
  inSelectedRow,
  inFillRange,
  isFillDragging,
  isExpanded,
  setSelectedCellRange,
  unresolvedComments,
  noComments,
  isFirstRow,
  isLastRow,
  useGreyBackground,
  hovered,
  showExpandIcon,
  formValues,
  editing,
  tempValue,
  setExpandModal,
  rowData,
  column,
  columnValuesCount,
  colourSettings,
  type,
  startRowId,
  finalRowId,
  finalRowNumber,
  startColumnId,
  finalColumnId,
  finalColumnNumber,
  allowComments,
  row,
  className: classNameProp,
  rowLevelColour,
  headerColourOverride,
  treeIndentPx,
  cellIssue,
  onRowNumberSelect,
  openRowMenu
}) => {
  const themed = useThemedColours()
  const cellRef = useRef<HTMLDivElement>(null)
  const savingCellRef = useRef<any>(null)
  const { setSnackbarMessage, displayErrorMessage } = useApplicationStore()
  const { project } = useProject()

  const [validation, setValidation] = useState<{
    error: boolean
    warn: boolean
    errorMessage: string
  }>({
    error: false,
    warn: false,
    errorMessage: ''
  })

  const [currentValue, updateCurrentValue] = useState<ICellValue>(value)
  const updateCurrentValueCallback = (newValue: ICellValue) => {
    updateCurrentValue(newValue)
  }

  // Check if the status of the selected cell when mounting the component
  useEffect(() => {
    if (editing && tempValue !== undefined) {
      updateCurrentValueCallback(tempValue)
    }
  }, [])

  // Update the state of the selected cell if the value of the cell changes
  useLayoutEffect(() => {
    if (
      rowId &&
      columnId &&
      rowNumber !== undefined &&
      columnNumber !== undefined &&
      selected &&
      editing &&
      setSelectedCell &&
      tempValue !== undefined &&
      !checkValuesSame(tempValue, currentValue, kind)
    ) {
      setSelectedCell({ rowId, columnId, rowNumber, columnNumber, editing, value: currentValue })
    }
  }, [currentValue])

  // when updating a column we need to update the current value of a cell if value changes.
  useLayoutEffect(() => {
    if (loading) return
    const newValue = editing ? currentValue : value
    if (!checkValuesSame(newValue, currentValue, kind)) {
      updateCurrentValueCallback(newValue)
    }
    if (
      selected &&
      editing &&
      setSelectedCell &&
      rowId &&
      columnId &&
      rowNumber !== undefined &&
      columnNumber !== undefined &&
      tempValue !== undefined &&
      !checkValuesSame(tempValue, newValue, kind)
    ) {
      setSelectedCell({ editing, rowId, columnId, rowNumber, columnNumber, value: newValue })
    }
  }, [loading, value])

  useLayoutEffect(() => {
    if (selected && !editing) {
      cellRef.current?.focus({ preventScroll: true })
    }
  }, [selected])

  const setCurrentValue = (newValue: ICellValue, onSuccess?: () => void, onError?: (message?: string) => void) => {
    // Give back focus to cell to select column types (only for single select, not multiselect)
    if (kind === 'select') {
      cellRef.current?.focus()
    }
    // Run Validation Checks

    if (column && rowId) {
      const validValue = isValidValue(newValue, rowData, columnValuesCount, column, formValues, type, true)
      if (validValue.error) {
        if (validValue.warn) setSnackbarMessage({ status: 'warning', message: validValue.errorMessage })
        else {
          updateCurrentValueCallback(value)
          setSnackbarMessage({ status: 'error', message: validValue.errorMessage })
          if (onError) onError()
          return
        }
      }
    }

    if (
      kind !== 'multilink' &&
      kind !== 'attachment' &&
      kind !== 'select' &&
      kind !== 'multiselect' &&
      kind !== 'vote' &&
      kind !== 'json'
    ) {
      const result = validateField(kind, newValue, columnName!)
      if (!(result as IResultFieldValidation).pass) {
        if (onError) onError()
        updateCurrentValueCallback(value)
        setSnackbarMessage({ status: 'error', message: (result as IResultFieldValidation).error })
        return
      }
    }

    if (checkValuesSame(value, newValue, kind)) return

    // --- Optimistic update for select/multiselect so bg reacts immediately ---
    const isSelect = SELECT_COLUMN_TYPES.includes(kind)
    if (isSelect) {
      // show the chip colour and suppress cell bg rules right away
      updateCurrentValueCallback(newValue)
    }

    if (setCellValue) {
      const cell = {
        cells: [
          {
            columnId: columnId!,
            rowId: rowId!,
            value: newValue,
            oldValue: value
          }
        ],
        context: { projectId: project.publicId }
      }

      setCellValue(
        cell,
        () => {
          if (onSuccess) onSuccess()
          if (['multilink', 'attachment', 'vote', 'json'].includes(kind)) updateCurrentValueCallback(newValue)
        },
        (error: any) => {
          displayErrorMessage(error)
          if (onError) onError()
          updateCurrentValueCallback(value)
        }
      )
    }
  }

  const handleSaveValue = () => {
    if (savingCellRef.current !== null) {
      clearTimeout(savingCellRef.current)
    }

    savingCellRef.current = setTimeout(() => {
      savingCellRef.current = null
      const isValueEmpty = value === null || value === ''

      if (
        (TEXT_COLUMN_TYPES.includes(kind) || kind === 'link' || DATE_COLUMN_TYPES.includes(kind)) &&
        !isSameValueStringValue(currentValue as string, isValueEmpty ? null : (value as string))
      ) {
        setCurrentValue(currentValue)
      } else if (
        NUMBER_COLUMN_TYPES.includes(kind) &&
        !isSameValueStringValue(currentValue as string, isValueEmpty ? null : (value as number))
      ) {
        setCurrentValue(
          currentValue === undefined || currentValue === null || currentValue === '' ? null : Number(currentValue)
        )
      }
    }, 150)
  }

  useEffect(() => {
    if (!selected && !scriptEnabled && !formulaEnabled && !isJoined) {
      handleSaveValue()
    }
  }, [selected])

  // Extract the specific count for this column+value as a stable primitive dependency
  // instead of chaining expressions that short-circuit to different types each render
  // Email columns are tallied under a lower-cased key, so read them back with the
  // same key or the count would always come out undefined.
  const currentValueStr = getDuplicateValueKey(
    currentValue?.toString() ?? '',
    isCaseInsensitiveDuplicateKind(column?.kind)
  )
  const currentValueCount =
    columnId && columnValuesCount[columnId] ? columnValuesCount[columnId][currentValueStr] : undefined

  useEffect(() => {
    if (column && rowId) {
      const validValue = isValidValue(currentValue, rowData, columnValuesCount, column, formValues, type)
      // `isValidValue` always returns a fresh object literal, so the naive
      // `setValidation(validValue)` form would queue a re-render on every effect
      // run even when the outcome is unchanged. Combined with `Row` re-allocating
      // `rowData` on every render, that produced cascading updates that hit
      // React's nested-update limit (Sentry WEBAPP-18, -1E, -24, -15).
      setValidation((prev) =>
        prev.error === validValue.error &&
        prev.warn === validValue.warn &&
        prev.errorMessage === validValue.errorMessage
          ? prev
          : validValue
      )
    }
  }, [
    currentValueCount,
    column?.validationNoBlanks,
    column?.validationNoDuplicates,
    column?.stringValidation,
    column?.hardValidation,
    column?.blockInvalidSelectValues,
    column?.isJoined,
    column?.kindOptions,
    currentValue,
    kind,
    rowData,
    formValues,
    type
  ])

  const textAlign = align ? align : 'left'
  const textColor = color ? color : 'inherit'
  const lockedByCellIssue = !!cellIssue
  // What the cell's mark says, if it has one: why a matrix cell is blank, or else a broken validation rule the
  // column shows a symbol for.
  const issue: { message: string; tone: CellIssueTone } | null = (() => {
    if (loading || editing) return null
    if (cellIssue) return { message: cellIssue, tone: 'info' }
    if (validation.error && column?.displayValidationError)
      return { message: validation.errorMessage, tone: validation.warn ? 'warning' : 'error' }
    return null
  })()
  const issueDescriptionId = useId()
  const isReadOnly =
    (locked !== undefined && locked) ||
    (readOnly !== undefined && readOnly) ||
    lockedByCellIssue ||
    !isContributor ||
    scriptEnabled ||
    formulaEnabled ||
    isJoined

  const handleOnContextMenu = (event: React.MouseEvent) => {
    // A form field goes into edit mode as soon as it's clicked, so a right-click on one usually
    // lands mid-edit. In a plain text box the form's menu works on the text being typed; any other
    // editor (the JSON editor, an open dropdown) keeps the browser's own menu.
    const activeElement = document.activeElement
    const editingFormField = editing && isExpanded && contextClick !== undefined
    const editingText =
      editingFormField &&
      (activeElement instanceof HTMLInputElement || activeElement instanceof HTMLTextAreaElement) &&
      !!cellRef.current?.contains(activeElement)
    if (editingFormField && !editingText) return

    event.preventDefault()

    // The row-number cell has no columnId, so it can't go through the cell-menu path below.
    // Hand it to the row-selection handler, which decides between the bulk and single-row menus.
    if (isRowNumberCell) {
      if (openRowMenu) openRowMenu(event)
      return
    }

    if (!editing || editingText) {
      if (
        contextClick &&
        setSelectedCell &&
        rowNumber !== undefined &&
        columnNumber !== undefined &&
        rowId !== undefined &&
        columnId !== undefined
      ) {
        if (!inSelectedCellRange && !editingText) {
          // A form has no cell ranges, so it passes no range setter.
          setSelectedCellRange?.(INITIAL_SELECTED_CELL_RANGE)
          setSelectedCell({ rowId, columnId, rowNumber, columnNumber, editing, value })
        }

        contextClick({
          open: true,
          top: `${event.clientY + 5}px`,
          left: `${event.clientX - 120}px`,
          right: 'auto',
          bottom: 'auto',
          columnNumber: columnNumber,
          columnId: columnId,
          rowNumber: rowNumber,
          rowId: rowId
        })
      }
    }
  }

  const handleOnMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.button === 0) {
      if (setSelectedCellRange && rowNumber !== undefined && columnNumber !== undefined) {
        setSelectedCellRange(INITIAL_SELECTED_CELL_RANGE)
      }
      if (!editing) {
        if (
          setSelectedCell &&
          setIsFocused &&
          rowId !== undefined &&
          columnId !== undefined &&
          rowNumber !== undefined &&
          columnNumber !== undefined
        ) {
          cellRef.current?.focus()
          setIsFocused(true)
          setSelectedCell({ rowId, columnId, rowNumber, columnNumber, editing, value })
          // Signal drag start for auto-scroll functionality
          if (setIsDragging) {
            setIsDragging(true)
          }
        }
        // In form view (isExpanded) we enter edit mode on mousedown so text /
        // number cells get an input immediately. Skip select cells: their
        // editing prop is wired to opening the dropdown menu (see
        // SelectCell), so auto-entering edit mode here would race with the
        // user's click on the dropdown button — both paths set
        // selectCellMenu, and the menu briefly opens then closes again.
        if (isExpanded && !SELECT_COLUMN_TYPES.includes(kind)) {
          handleChangeEditMode(true)
        }
      }
    }
  }

  const handleOnMouseOver = (event: React.MouseEvent) => {
    if (
      setIsFocused &&
      event.buttons > 0 &&
      setSelectedCellRange &&
      !isFillDragging &&
      rowNumber !== undefined &&
      columnNumber !== undefined &&
      columnId !== undefined
    ) {
      setSelectedCellRange({ endRowIndex: rowNumber, endColumnIndex: columnNumber, endColumnId: columnId })
    }
  }

  const handleOnDragStart = (event: React.DragEvent<HTMLDivElement>) => {
    if (canDrag && rowId !== undefined) {
      // Clear any existing selections
      if (setSelectedCellRange && setSelectedCell) {
        setSelectedCellRange(INITIAL_SELECTED_CELL_RANGE)
        setSelectedCell(INITIAL_SELECTED_CELL)
      }
      event.dataTransfer.setData('rowId', rowId)
      event.dataTransfer.effectAllowed = 'move'
    }
  }

  const handleOnKeyDown = (event: React.KeyboardEvent) => {
    // Start the start point of a cell selection if shift is held down.
    // Not while this row is part of a whole-row selection: there Shift+Arrow extends the row
    // selection instead, and a cell range would draw a competing outline inside it.
    if (
      setSelectedCellRange &&
      rowNumber !== undefined &&
      columnNumber !== undefined &&
      columnId !== undefined &&
      event.shiftKey &&
      event.key === 'Shift' &&
      !editing &&
      !inSelectedRow
    ) {
      setSelectedCellRange({ endRowIndex: rowNumber, endColumnIndex: columnNumber, endColumnId: columnId })
    }

    if (editing) {
      if (
        INPUT_COLUMN_OPTION_TYPES.includes(kind) ||
        SELECT_COLUMN_TYPES.includes(kind) ||
        ATTACHMENT_COLUMN_TYPES.includes(kind) ||
        VOTE_COLUMN_TYPES.includes(kind) ||
        kind === 'multilink'
      ) {
        if (event.key === 'Enter' && !(event.ctrlKey || event.altKey || event.metaKey)) {
          if (kind === 'multilink') {
            // The inline input commits its own value; leave edit mode, refocus the cell and
            // move to the row below so the grid keeps focus — matching text/number cells
            // (see handleKeyDownCell). Plain handleEditChange would drop focus off the grid.
            handleChangeEditMode(false, { moveToNextCell: true, saveValue: true })
          } else {
            handleEditChange(false)
          }
        } else if (event.key === 'Tab') {
          handleEditChange(false)
          fireKeyDownEvent(event.shiftKey ? 'ArrowLeft' : 'ArrowRight')
        } else if (event.key === 'Escape') {
          handleChangeEditMode(false, { saveValue: false })
        }
      } else if (kind === 'json' && event.key === 'Escape') {
        // JSON uses code-editor semantics: plain Enter inserts a newline (handled
        // by CodeMirror), Cmd/Ctrl+Enter commits and then leaves edit mode through
        // `onExitEditing` (handled inside JsonCell), and
        // clicking away commits on blur. Only Escape (cancel) is handled here.
        handleChangeEditMode(false, { saveValue: false })
      }

      // Prevent any other key events when in editing mode
      return false
    }

    if (handleKeyDown) {
      handleKeyDown(event, {
        currentRowId: rowId ? rowId : '',
        currentColumnId: columnId ? columnId : '',
        nextRowId: nextRowId ? nextRowId : '',
        nextColumnId: nextColumnId ? nextColumnId : '',
        prevRowId: prevRowId ? prevRowId : '',
        prevColumnId: prevColumnId ? prevColumnId : '',
        startRowId: startRowId ? startRowId : '',
        finalRowId: finalRowId ? finalRowId : '',
        finalRowNumber: finalRowNumber !== undefined ? finalRowNumber - 1 : -1,
        startColumnId: startColumnId ? startColumnId : '',
        finalColumnId: finalColumnId ? finalColumnId : '',
        finalColumnNumber: finalColumnNumber !== undefined ? finalColumnNumber - 1 : -1
      })
    }

    // Select / multiselect: switch the cell into editing mode synchronously and
    // let the SelectCell open its own menu in response to the editing prop.
    const openSelectDropdown = (): boolean => {
      if (
        !SELECT_COLUMN_TYPES.includes(kind) ||
        isReadOnly ||
        !setSelectedCell ||
        !rowId ||
        !columnId ||
        rowNumber === undefined ||
        columnNumber === undefined
      ) {
        return false
      }
      setSelectedCell({ rowId, columnId, rowNumber, columnNumber, value, editing: true })
      return true
    }

    // SHIFT+SPACE is the row-selection shortcut, handled by the grid-level ``handleKeyDown``
    // above. It must not also toggle a checkbox, open a select dropdown, or enter edit mode.
    if (event.key === ' ' && event.shiftKey && !event.ctrlKey && !event.metaKey) return

    if (event.key === 'Enter') {
      // if key pressed is enter and cell is a select column then the menu options should open
      // similar to when double clicking
      if (SELECT_COLUMN_TYPES.includes(kind)) {
        openSelectDropdown()
      } else if (kind === 'multilink' && !isReadOnly) {
        handleChangeEditMode(true)
      } else if ((!INPUT_COLUMN_OPTION_TYPES.includes(kind) && kind !== 'json') || isReadOnly) {
        // if key pressed is enter and cell is a non editable column then the selected cell should move down
        fireKeyDownEvent('ArrowDown')
      }
    } else if (event.key === ' ') {
      event.preventDefault()
      // Important to keep space bar working in search
      event.stopPropagation()
      if (kind === 'checkbox' && !isReadOnly) {
        const current = !!currentValue
        setCurrentValue((!current).toString())
      } else if (kind === 'multilink' && !isReadOnly) {
        handleChangeEditMode(true)
      }

      openSelectDropdown()
    } else if (event.key === 'Tab') {
      // the selected cell should move right
      fireKeyDownEvent(event.shiftKey ? 'ArrowLeft' : 'ArrowRight')
    }

    // Check if the META/CTRL key is not being pressed along with a character in order to avoid changing the status of the cell to editing when
    // the user only wanted to do for example CTRL+C/CTRL+V/CTRL+X
    const isChar = !event.metaKey && !event.ctrlKey && event.key.length === 1
    if (
      ((event.key === 'Enter' && !(event.ctrlKey || event.altKey || event.metaKey)) || isChar) &&
      (INPUT_COLUMN_OPTION_TYPES.includes(kind) || kind === 'json') &&
      !isReadOnly
    ) {
      handleChangeEditMode(true)

      // Prevent the editor from inserting a newline when switching to edit mode for text / JSON cells
      if (event.key === 'Enter' && (kind === 'text' || kind === 'json')) {
        event.preventDefault()
      }
    }

    // Typing a printable character on an empty Multi-URL cell enters inline edit mode so the
    // character flows into the auto-focused input — matching text/number cells. Only for empty cells.
    const multilinkHasNoLinks = !Array.isArray(currentValue) || currentValue.length === 0
    if (isChar && event.key !== ' ' && kind === 'multilink' && !isReadOnly && multilinkHasNoLinks) {
      handleChangeEditMode(true)
    }
  }

  const fireKeyDownEvent = (key: 'ArrowDown' | 'ArrowRight' | 'ArrowLeft') => {
    const keyDown = new KeyboardEvent('keydown', {
      bubbles: true,
      cancelable: true,
      key: key,
      shiftKey: false
    })
    // Wrapped within timeout, because we want any pending state updates in React to
    // be commited before the event is fired - as the handleKeyDown function relies
    // on local state being up to date
    setTimeout(() => cellRef.current?.dispatchEvent(keyDown), 0)
  }

  const handleChangeEditMode = (editing: boolean, options?: IEditCompleteOptions) => {
    handleEditChange(editing)

    // When we switch from edit mode to "normal" mode, we need to set focus on this
    // cell component otherwise onKeyDown events are not caught by this component
    if (editing === false) {
      setTimeout(() => cellRef.current?.focus(), 0)
      if (options && options.saveValue === false) {
        updateCurrentValueCallback(value)
      } else handleSaveValue()
      if (options && options.moveToNextCell) {
        fireKeyDownEvent('ArrowDown')
      }
    }
  }

  const hasUnresolvedComments = unresolvedComments !== undefined && unresolvedComments > 0
  const hasRowComments = noComments !== undefined && noComments > 0
  const expandTooltip =
    allowComments && hasRowComments
      ? hasUnresolvedComments
        ? 'Expand Row / Show Comments'
        : 'Expand Row / Show Resolved Comments'
      : 'Expand Row'
  const isRowNumberCell = columnId === undefined

  const cellId = isRowNumberCell
    ? `numbercell/${rowId}`
    : createCellId(
        rowId !== undefined ? rowId : 'undefined',
        columnId !== undefined ? columnId : 'undefined',
        uniqueNumber
      )
  const isEditingText = editing && (kind === 'text' || kind === 'json')
  const isExpandedTextColumn = (kind === 'text' || kind === 'multiselect') && isExpanded && value

  const handleOnCommentsClick = () => {
    if (setExpandModal && rowId && rowNumber !== undefined && setSelectedCellRange && setSelectedCell) {
      setSelectedCell(INITIAL_SELECTED_CELL)
      setSelectedCellRange(INITIAL_SELECTED_CELL_RANGE)
      setExpandModal!(true, true, rowId, rowNumber)
    }
  }

  const handleOnExpandClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    if (setExpandModal && rowId && rowNumber !== undefined) {
      setExpandModal(true, false, rowId, rowNumber)
    }
  }

  const handleKeyDownCell = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === 'Tab') {
      handleChangeEditMode(false, {
        moveToNextCell: event.key === 'Enter',
        saveValue: true
      })
    } else if (event.key === 'Escape') {
      handleChangeEditMode(false, { moveToNextCell: false, saveValue: false })
    }
  }

  const handleEditChange = (newEditing: boolean) => {
    if (setSelectedCell && rowId && columnId && rowNumber !== undefined && columnNumber !== undefined) {
      setSelectedCell({ rowId, columnId, rowNumber, columnNumber, editing: newEditing, value })
    }
  }

  const handleOnBlur = () => {
    if (selected && editing) {
      handleSaveValue()
    }
  }

  // Clicking a row number builds the whole-row selection, which also selects that row's cells —
  // ``handleRowNumberSelect`` in the data context owns both halves, so there is nothing to set
  // here beyond reporting the modifiers.
  const handleOnClick = (event: React.MouseEvent) => {
    if (!isRowNumberCell || !onRowNumberSelect) return
    onRowNumberSelect({ shiftKey: event.shiftKey, toggleKey: event.metaKey || event.ctrlKey })
  }

  const handleOnDoubleClick = () => {
    if (isExpanded || isReadOnly) return
    if (rowId && columnId && rowNumber !== undefined && columnNumber !== undefined) {
      setSelectedCell!({ rowId, columnId, rowNumber, columnNumber, value, editing: true })
    }
  }

  // In a form the cell is a field, drawn as a text field or a checkbox rather than a grid cell.
  const asFormField = !!isExpanded && type === ViewTypes.FORM

  const cellColour =
    column && !headerColourOverride
      ? getCellColour(
          column,
          columnValuesCount,
          row,
          (kind === 'multiselect' && Array.isArray(currentValue) && currentValue.length > 0) ||
            (kind === 'select' && !!currentValue)
            ? undefined
            : value,
          colourSettings,
          locked || lockedByCellIssue || scriptEnabled || formulaEnabled || isJoined || useGreyBackground,
          // A form's field sits on the field fill, as a text field does, rather than the grid's. A checkbox
          // and an attachment's files have no field around them, so they stay on the form's own.
          asFormField && !FRAMELESS_FORM_KINDS.includes(kind)
            ? 'var(--color-field)'
            : hovered
              ? ROW_HOVER_FILL
              : undefined,
          rowLevelColour,
          rowData
        )
      : null

  // A transposed view's Field cell stands in for a column header, so it takes
  // the header's colours by the header's rules.
  const colourStyle: React.CSSProperties = headerColourOverride
    ? themed.header(headerColourOverride.backgroundColour, headerColourOverride.fontColour)
    : {
        backgroundColor:
          themed.fill(cellColour?.backgroundColour) || (isRowNumberCell ? 'var(--color-surface)' : undefined),
        color: themed.text(cellColour?.fontColour, cellColour?.backgroundColour)
      }

  // Only the expanded row and form fields let a read-only value's text be highlighted. In the
  // grid a locked cell stays select-none like any other, so dragging across it selects cells.
  const isCopyTextHost = isReadOnly && !isRowNumberCell && !!isExpanded

  const containerClassName = styles.container({
    searched,
    readOnly: locked || lockedByCellIssue || scriptEnabled || formulaEnabled || isJoined,
    selectableText: isCopyTextHost,
    expanded: isExpanded,
    // Anchor cell keeps its original background so the user can see which cell they clicked first;
    // the range tint (inRange) applies to every OTHER cell in the selection.
    inRange: inSelectedCellRange && !selected,
    inSelectedColumn,
    inSelectedRow,
    inFillRange,
    frozen,
    editingText: isEditingText,
    editing,
    canDrag,
    expandedText: !!isExpandedTextColumn,
    lastFrozen: isLastFrozenCell,
    rowNumberCell: isRowNumberCell,
    firstRow: isFirstRow,
    lastRow: isLastRow,
    fontBold: cellColour?.fontBold,
    fontItalic: cellColour?.fontItalic,
    fontUnderline: cellColour?.fontUnderline,
    class: `${getRowHeightClassName(rowHeight)} ${getRowMaxHeightClassName(rowHeight)} ${hasUnresolvedComments ? styles.comments() : 'table box-border'} ${classNameProp || ''}`
  })

  return (
    <div
      ref={cellRef}
      id={cellId}
      data-testid={`spreadsheet-${isRowNumberCell ? 'row-number-' : ''}cell`}
      data-tour={rowNumber === 0 && columnNumber === 0 ? 'table-first-cell' : undefined}
      data-columnid={columnId}
      aria-describedby={issue ? issueDescriptionId : undefined}
      data-copy-text={isCopyTextHost ? '' : undefined}
      className={containerClassName}
      style={{
        width: isExpanded && kind === 'attachment' ? 'unset' : width !== undefined ? `${width}px` : '100%',
        ...colourStyle,
        // Row numbers are one small size all the way down; shrinking them per digit
        // made the column step down in size at row 10, 100, ...
        fontSize: isRowNumberCell ? '12px' : undefined,
        ...(frozen ? { left: `${cumulativeWidth}px` } : { transform: `translateX(${cumulativeWidth}px)` }),
        ...({ '--cell-width': width !== undefined ? `${width}px` : '100%' } as React.CSSProperties),
        ...(treeIndentPx ? ({ '--cell-pl': `${treeIndentPx}px` } as React.CSSProperties) : {})
      }}
      onMouseDown={handleOnMouseDown}
      //eslint-disable-next-line jsx-a11y/mouse-events-have-key-events
      onMouseOver={handleOnMouseOver}
      tabIndex={rowNumber !== undefined && columnNumber !== undefined ? rowNumber * columnNumber : undefined}
      draggable={canDrag}
      onDragStart={handleOnDragStart}
      onKeyDown={handleOnKeyDown}
      onContextMenu={handleOnContextMenu}
      onClick={handleOnClick}
      onDoubleClick={handleOnDoubleClick}
      onBlur={handleOnBlur}
    >
      {!loading && (
        <React.Fragment>
          {TEXT_COLUMN_TYPES.includes(kind) && (typeof value === 'string' || !value || value.toString()) && (
            <TextCell
              value={currentValue as string}
              align={textAlign}
              color={textColor}
              kind={kind}
              readOnly={isReadOnly}
              setCellValue={updateCurrentValueCallback}
              editing={editing}
              handleKeyDown={handleKeyDownCell}
              rowHeight={rowHeight!}
              selected={selected!}
              isExpanded={isExpanded}
              validation={validation.error}
            />
          )}
          {/* Also accept string values: converting a text column with numeric text to a number column rewrites
              the stored data on the backend, but local state keeps the original strings until a refetch. The
              previous `typeof value === 'number'` guard rejected those strings, so NumberCell never mounted and
              the cell was blank/uneditable until refresh (#10272). NumberCell can display numeric strings and converts user input via Number(). */}
          {NUMBER_COLUMN_TYPES.includes(kind) && (typeof value === 'number' || typeof value === 'string' || !value) && (
            <NumberCell
              value={currentValue as number}
              color={textColor}
              kind={kind}
              readOnly={isReadOnly}
              setCellValue={updateCurrentValueCallback}
              editing={editing}
              handleKeyDown={handleKeyDownCell}
              validation={validation.error}
              rowHeight={rowHeight!}
              selected={selected!}
              thousandSeparator={column ? column.thousandSeparator : true}
              decimalPlaces={column ? column.decimalPlaces : 2}
            />
          )}
          {DATE_COLUMN_TYPES.includes(kind) && (typeof value === 'string' || !value) && (
            <DateCell
              value={currentValue as string}
              align={textAlign}
              color={textColor}
              kind={kind}
              readOnly={isReadOnly}
              setCellValue={updateCurrentValueCallback}
              handleKeyDown={handleKeyDownCell}
              editing={editing}
              rowHeight={rowHeight!}
              selected={selected!}
              validation={validation.error}
              dateFormat={column ? column.dateFormat : null}
              asFormField={asFormField}
              onPick={setCurrentValue}
            />
          )}
          {kind === 'link' && (typeof value === 'string' || !value) && (
            <LinkCell
              value={currentValue as string}
              align={textAlign}
              color={textColor}
              kind={kind}
              readOnly={isReadOnly}
              setCellValue={updateCurrentValueCallback}
              editing={editing}
              handleKeyDown={handleKeyDownCell}
              rowHeight={rowHeight!}
              selected={selected!}
              isExpanded={isExpanded}
              validation={validation.error}
              type={type}
              displayLink={column ? column.displayLink : false}
            />
          )}
          {kind === 'multilink' && (
            <MultiLinkCell
              value={currentValue as string[]}
              align={textAlign}
              color={textColor}
              kind={kind}
              readOnly={isReadOnly}
              isAdmin={isAdmin}
              isContributor={isContributor}
              setCellValue={setCurrentValue}
              editing={editing}
              rowHeight={rowHeight!}
              selected={selected!}
              isExpanded={isExpanded}
              validation={validation.error}
              onExitEditing={() => handleChangeEditMode(false)}
            />
          )}
          {kind === 'vote' && (
            <VoteCell
              value={currentValue as IVote[]}
              align={textAlign}
              color={textColor}
              readOnly={isReadOnly}
              isAdmin={isAdmin}
              isContributor={isContributor}
              setCellValue={setCurrentValue}
              width={width!}
              rowHeight={rowHeight!}
              selected={selected!}
              rowNumber={rowNumber}
              isExpanded={isExpanded}
              validation={validation.error}
            />
          )}
          {kind === 'checkbox' && (
            <CheckboxCell
              value={currentValue as boolean}
              align={'center'}
              color={textColor}
              readOnly={isReadOnly}
              setCellValue={setCurrentValue}
              handleKeyDown={handleKeyDownCell}
              rowHeight={rowHeight!}
              selected={selected!}
              asFormField={asFormField}
              label={columnName}
            />
          )}

          {ATTACHMENT_COLUMN_TYPES.includes(kind) && (
            <AttachmentCell
              value={currentValue as IFile[]}
              align={textAlign}
              color={textColor}
              readOnly={isReadOnly}
              isAdmin={isAdmin}
              isContributor={isContributor}
              setCellValue={setCurrentValue}
              rowHeight={rowHeight!}
              selected={selected!}
              columnId={columnId}
              isExpanded={isExpanded}
              asFormField={asFormField}
              label={columnName}
            />
          )}
          {SELECT_COLUMN_TYPES.includes(kind) && (
            <SelectCell
              value={currentValue as string}
              align={textAlign}
              color={textColor}
              kind={kind}
              readOnly={isReadOnly}
              isAdmin={isAdmin}
              isContributor={isContributor}
              setCellValue={setCurrentValue}
              rowHeight={rowHeight!}
              selected={selected!}
              editing={editing}
              isExpanded={isExpanded}
              onCloseSelect={() => handleChangeEditMode(false)}
              formValues={formValues}
              validation={validation.error}
              isJoined={isJoined}
              colourSettings={colourSettings}
              type={type}
              column={column!}
              rowData={rowData}
              columnValuesCount={columnValuesCount}
              row={row}
              rowLevelColour={rowLevelColour}
            />
          )}
          {kind === 'json' && (
            <JsonCell
              value={currentValue as IJsonCellValue | null}
              align={textAlign}
              color={textColor}
              readOnly={isReadOnly}
              rowHeight={rowHeight!}
              selected={selected!}
              editing={editing}
              isExpanded={isExpanded ?? false}
              columnId={columnId}
              rowId={rowId}
              setCellValue={
                setCurrentValue as (
                  value: IJsonCellValue | null,
                  onSuccess?: () => void,
                  onError?: (message?: string) => void
                ) => void
              }
              onValidationError={(message) => setSnackbarMessage({ status: 'error', message })}
              onExitEditing={() => handleChangeEditMode(false)}
            />
          )}

          {issue && (
            <CellIssue
              message={issue.message}
              tone={issue.tone}
              descriptionId={issueDescriptionId}
              selected={!!selected}
              rowHeight={rowHeight}
            />
          )}
        </React.Fragment>
      )}
      {loading && width && (
        <div>
          <span
            className="skeleton-box"
            style={{ width: `${width}px`, height: `${getRowHeightVariable(rowHeight) - 6}px` }}
          />
        </div>
      )}

      {isRowNumberCell && showExpandIcon && !loading && rowId && rowNumber !== undefined && (
        <Tooltip content={expandTooltip}>
          <Button
            internalType="outline"
            data-testid="row-number-expand"
            className="h-auto absolute right-[3px] top-1/2 z-[2] -translate-y-1/2 rounded p-1"
            onMouseDown={(event) => event.stopPropagation()}
            onClick={handleOnExpandClick}
          >
            <Expand className="h-3 w-3" />
            {/* When the row has comments, badge the button's top-right corner with the count so the
                comment state stays visible while the button covers the standalone indicator. */}
            {allowComments && hasRowComments && (
              <span
                data-testid="row-number-expand-comment-badge"
                className={`absolute -right-1.5 -top-1.5 flex h-3.5 min-w-3.5 items-center justify-center rounded-full border border-divider px-0.5 text-[9px] font-semibold leading-none ${hasUnresolvedComments ? 'bg-light-pink' : 'bg-light-blue'}`}
              >
                {hasUnresolvedComments ? unresolvedComments : noComments}
              </span>
            )}
          </Button>
        </Tooltip>
      )}

      {allowComments && noComments !== undefined && unresolvedComments !== undefined && (
        <div
          data-testid="row-number-comments"
          // Over the cell's right edge, as the expand button is, rather than
          // beside the number: in the flow it pushed every row number off
          // centre, comments or not.
          className={`absolute right-[3px] top-1/2 -translate-y-1/2 z-[1] flex items-center justify-center text-sm rounded cursor-pointer border-box p-0.5 min-w-[19px] ${showExpandIcon ? 'invisible' : ''} ${unresolvedComments > 0 ? 'bg-light-pink' : noComments > 0 ? 'bg-light-blue' : 'bg-transparent'}`}
          onClick={handleOnCommentsClick}
        >
          {unresolvedComments > 0 ? (
            unresolvedComments.toString()
          ) : noComments > 0 ? (
            // All threads resolved: show the resolved count (== total, since none are unresolved).
            noComments.toString()
          ) : (
            <span>&nbsp;</span>
          )}
        </div>
      )}
    </div>
  )
}

// Memoised on props: the parent re-renders on every scroll frame (the viewport window moves), but
// a cell whose props have not changed must not re-render with it. Context changes still
// reach it (memo only compares props).
export default React.memo(Cell)
